import { sendImportRemoteRuntimeEvent } from './events';
import { getAllRemotesRuntime, getRemoteRuntime } from './runtime';
import { loadRemoteModule } from '../helpers/loadRemoteModule';
import { promiseWithRetries } from '../helpers/promiseWithRetries';
import { setVersionOnEntriesFile } from '../helpers/setVersionOnEntriesFile';
import type { RuntimeRemoteInfo } from '../internal';

const MAX_RETRIES = 3;
const IMPORT_REMOTE_TIMEOUT_MS = 20_000;

export interface ImportRemoteParams {
  scope: string;
  module: string;
  version?: string;
  /**
   * Milliseconds before the remote import is rejected.
   * Defaults to 20,000; values <= 0 expire immediately.
   * Set to false to disable the deadline.
   */
  timeoutMs?: number | false;
  onRetry?: (params: {
    error: unknown;
    tryAttempt: number;
    retryName: string;
  }) => void;
}

const IMPORT_REMOTE_RETRY_TYPES = {
  GET_REMOTE: 'IMPORT_REMOTE_RETRY__GET_REMOTE',
  RESOLVE_REMOTE: 'IMPORT_REMOTE_RETRY__RESOLVE_REMOTE',
} as const;

export async function importRemote<T>({
  scope,
  module,
  version,
  timeoutMs = IMPORT_REMOTE_TIMEOUT_MS,
  onRetry = () => {},
}: ImportRemoteParams) {
  // Named in the deadline error so telemetry can tell which step hung.
  let pendingStep = 'remote lookup attempt 1';
  let deadlineError: Error | undefined;
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
  const deadlinePromise =
    timeoutMs === false
      ? undefined
      : new Promise<never>((_resolve, reject) => {
          deadlineTimer = globalThis.setTimeout(() => {
            deadlineError = new Error(
              `ExoSkeletonAPI Error: Failed to import remote "${module}" from scope "${scope}" within ${timeoutMs} milliseconds (${pendingStep})`,
            );
            reject(deadlineError);
          }, timeoutMs);
        });
  const throwIfDeadlineExpired = () => {
    if (deadlineError) {
      throw deadlineError;
    }
  };

  const importPromise = (async () => {
    const performanceMark = globalThis.performance.mark('importRemoteStart');

    let remote: RuntimeRemoteInfo;

    try {
      const scopeRuntimeInfoResponse = await promiseWithRetries(
        async ({ attempt }) => {
          throwIfDeadlineExpired();
          pendingStep = `remote lookup attempt ${attempt}`;
          const remoteRuntimeInfo = getRemoteRuntime(scope);

          if (!remoteRuntimeInfo) {
            throw new Error(
              `ExoSkeletonAPI Error: No remote found for scope "${scope}"`,
            );
          }

          let url: string;

          if (!remoteRuntimeInfo.hasOwnProperty('url')) {
            console.warn(
              'Deprecation warning: getting remote URL in async way',
            );
            url = (await remoteRuntimeInfo.get()).url;
          } else {
            url = remoteRuntimeInfo.url;
          }

          return {
            ...remoteRuntimeInfo,
            url,
          } satisfies RuntimeRemoteInfo;
        },
        {
          retries: MAX_RETRIES,
          onRetry(error, tryAttempt) {
            throwIfDeadlineExpired();
            return onRetry({
              error,
              tryAttempt,
              retryName: IMPORT_REMOTE_RETRY_TYPES.GET_REMOTE,
            });
          },
        },
      );

      remote = scopeRuntimeInfoResponse;
    } catch (cause) {
      if (deadlineError && cause === deadlineError) {
        throw cause;
      }

      const remotes = getAllRemotesRuntime();

      const remotesToDisplay = Object.keys(remotes).map((scope: string) => {
        return {
          scope,
          url: remotes[scope as keyof typeof remotes].url,
        };
      });

      throw new Error(
        `ExoSkeletonAPI Error: Failed to get the remote for scope "${scope}"\n${cause}\nremotes: ${JSON.stringify(remotesToDisplay, null, 2)}`,
        { cause },
      );
    }

    const { url: rawUrl, override } = remote;
    const url =
      version && !override
        ? setVersionOnEntriesFile({
            url: rawUrl,
            version,
          })
        : rawUrl;

    try {
      let importTryAttempt = 0;
      const resolvedPromise = await promiseWithRetries(
        ({ attempt }) => {
          throwIfDeadlineExpired();
          pendingStep = `module load attempt ${attempt}`;
          return loadRemoteModule<T>({
            bustRemoteEntryCache: attempt > 1,
            scope,
            url,
            module,
            version,
          });
        },
        {
          retries: MAX_RETRIES,
          onRetry(error, tryAttempt) {
            throwIfDeadlineExpired();
            importTryAttempt = tryAttempt;

            return onRetry({
              error,
              tryAttempt,
              retryName: IMPORT_REMOTE_RETRY_TYPES.RESOLVE_REMOTE,
            });
          },
        },
      );
      throwIfDeadlineExpired();

      void sendImportRemoteRuntimeEvent({
        scope,
        metadata: {
          url,
          retryAttempt: importTryAttempt,
          timeToImport:
            globalThis.performance.now() - performanceMark.startTime,
        },
      });

      return resolvedPromise;
    } catch (cause) {
      if (deadlineError && cause === deadlineError) {
        throw cause;
      }

      throw new Error(
        `ExoSkeletonAPI Error: Failed to resolve remote "${module}" from scope "${scope}"\n${cause}`,
        { cause },
      );
    } finally {
      globalThis.performance.clearMarks('importRemoteStart');
    }
  })();

  try {
    if (!deadlinePromise) {
      return await importPromise;
    }

    return await Promise.race([importPromise, deadlinePromise]);
  } finally {
    if (deadlineTimer !== undefined) {
      globalThis.clearTimeout(deadlineTimer);
    }
  }
}
